import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock, LockOpen, Plus, Trash, PencilSimple, ShieldCheck, Key, FileXls } from "@phosphor-icons/react";

const SS_KEY = "cbvac.validation_token";
const SS_EXP = "cbvac.validation_token_exp";

function tokenValid() {
  const t = sessionStorage.getItem(SS_KEY);
  const exp = parseInt(sessionStorage.getItem(SS_EXP) || "0", 10);
  return !!t && Date.now() < exp;
}
function authHeaders() {
  const t = sessionStorage.getItem(SS_KEY);
  return t ? { "X-Validation-Token": t } : {};
}

const EMPTY = { first_name: "", last_name: "", phone: "", email: "", plate_number: "", id_plaque_number: "", expiration_date: "", notes: "" };

export default function Validation() {
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState(tokenValid());
  // When unlocked with the default password, the backend returns
  // `must_change: true`. We surface that as a forced first-time rotate.
  const [forceChange, setForceChange] = useState(false);
  const [currentPw, setCurrentPw] = useState("");

  return (
    <AppLayout title="Validation Tool" subtitle="Password-protected vehicle / plate verification log — separate from the main personnel records">
      {forceChange ? (
        <ForceChangePassword
          currentPassword={currentPw}
          onChanged={() => { setForceChange(false); setCurrentPw(""); setUnlocked(true); }}
          onCancel={() => {
            setForceChange(false); setCurrentPw("");
            sessionStorage.removeItem(SS_KEY); sessionStorage.removeItem(SS_EXP);
            setUnlocked(false);
          }}
        />
      ) : !unlocked ? (
        <UnlockGate onUnlocked={() => setUnlocked(true)} onRequireChange={(pw) => { setCurrentPw(pw); setForceChange(true); }} />
      ) : (
        <ValidationApp user={user} onLocked={() => setUnlocked(false)} />
      )}
    </AppLayout>
  );
}

function UnlockGate({ onUnlocked, onRequireChange }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function unlock(e) {
    e.preventDefault();
    if (!password) { toast.error("Enter the Validation Tool password"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/validation/unlock", { password });
      sessionStorage.setItem(SS_KEY, data.token);
      sessionStorage.setItem(SS_EXP, String(Date.now() + (data.expires_in_minutes - 1) * 60_000));
      if (data.must_change) {
        // Default password is still in use — force a rotation now.
        onRequireChange(password);
      } else {
        toast.success("Unlocked");
        onUnlocked();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unlock failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="surface-grid max-w-md mx-auto p-6" data-testid="validation-gate">
      <div className="text-center mb-4">
        <div className="inline-flex p-3 rounded-sm bg-amber-100 border border-amber-300 mb-3">
          <Lock size={28} weight="bold" className="text-amber-700" />
        </div>
        <h2 className="font-display text-xl font-bold">Validation Tool · Locked</h2>
        <p className="text-xs text-slate-500 mt-1">This section is gated by a separate password configured by an admin.</p>
      </div>
      <form onSubmit={unlock} className="space-y-3">
        <div>
          <Label className="tab-tag">Password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm" autoFocus data-testid="validation-password" />
        </div>
        <Button type="submit" disabled={busy} className="w-full rounded-sm bg-slate-900 text-white" data-testid="validation-unlock">
          <LockOpen size={14} weight="bold" className="mr-1" />{busy ? "Unlocking…" : "Unlock"}
        </Button>
        <p className="text-[10px] text-slate-400 text-center">Sessions auto-lock after 4 hours of inactivity or when the browser tab closes.</p>
      </form>
    </div>
  );
}

/**
 * ForceChangePassword — shown the first time the Validation Tool is
 * unlocked with the default `bsvac727` password. The operator cannot
 * proceed to the records until they set a new password.
 */
function ForceChangePassword({ currentPassword, onChanged, onCancel }) {
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    if (next === "bsvac727") { toast.error("New password cannot be the default"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        "/validation/change-password",
        { current_password: currentPassword, new_password: next },
        { headers: authHeaders() },
      );
      if (data.token) {
        sessionStorage.setItem(SS_KEY, data.token);
        sessionStorage.setItem(SS_EXP, String(Date.now() + (data.expires_in_minutes - 1) * 60_000));
      }
      toast.success("Validation Tool password updated");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Password change failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="surface-grid max-w-md mx-auto p-6" data-testid="validation-force-change">
      <div className="text-center mb-4">
        <div className="inline-flex p-3 rounded-sm bg-rose-100 border border-rose-300 mb-3">
          <Key size={28} weight="bold" className="text-rose-700" />
        </div>
        <h2 className="font-display text-xl font-bold">Set a New Validation Password</h2>
        <p className="text-xs text-slate-600 mt-2 leading-relaxed">
          The Validation Tool is still using the default password (<span className="mono font-bold">bsvac727</span>). Choose a new password before continuing — minimum 8 characters.
        </p>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label className="tab-tag">New Password</Label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="rounded-sm" autoFocus data-testid="vfc-new" />
        </div>
        <div>
          <Label className="tab-tag">Confirm New Password</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-sm" data-testid="vfc-confirm" />
        </div>
        <Button type="submit" disabled={busy} className="w-full rounded-sm bg-slate-900 text-white" data-testid="vfc-submit">
          <Key size={14} weight="bold" className="mr-1" />{busy ? "Saving…" : "Set new password & continue"}
        </Button>
        <Button type="button" variant="ghost" className="w-full rounded-sm text-slate-500" onClick={onCancel} data-testid="vfc-cancel">
          Cancel
        </Button>
      </form>
    </div>
  );
}

function ValidationApp({ user, onLocked }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [pwOpen, setPwOpen] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/validation/records", { headers: authHeaders() });
      setRows(data);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem(SS_KEY); sessionStorage.removeItem(SS_EXP);
        toast.error("Session expired — please unlock again");
        onLocked();
      } else {
        toast.error(err.response?.data?.detail || "Failed to load");
      }
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  function startNew() { setEditing(null); setForm(EMPTY); setOpen(true); }
  function startEdit(r) {
    setEditing(r);
    setForm({
      first_name: r.first_name || "", last_name: r.last_name || "",
      phone: r.phone || "", email: r.email || "",
      plate_number: r.plate_number || "",
      id_plaque_number: r.id_plaque_number || "",
      expiration_date: r.expiration_date || "", notes: r.notes || "",
    });
    setOpen(true);
  }
  async function save(e) {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error("First and last name required"); return; }
    try {
      if (editing) {
        await api.patch(`/validation/records/${editing.id}`, form, { headers: authHeaders() });
        toast.success("Record updated");
      } else {
        await api.post("/validation/records", form, { headers: authHeaders() });
        toast.success("Record added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  }
  async function remove(r) {
    if (!window.confirm(`Delete validation record for ${r.first_name} ${r.last_name}?`)) return;
    try {
      await api.delete(`/validation/records/${r.id}`, { headers: authHeaders() });
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  }
  function lock() {
    sessionStorage.removeItem(SS_KEY); sessionStorage.removeItem(SS_EXP);
    onLocked();
  }

  async function downloadExcel() {
    try {
      const res = await api.get("/validation/export.xlsx", {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `bsvac-validation-${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem(SS_KEY); sessionStorage.removeItem(SS_EXP);
        toast.error("Session expired — please unlock again");
        onLocked();
      } else {
        toast.error(err.response?.data?.detail || "Export failed");
      }
    }
  }

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [r.first_name, r.last_name, r.plate_number, r.id_plaque_number, r.email, r.phone].filter(Boolean).join(" ").toLowerCase().includes(q);
  });

  // Highlight expiring rows
  const today = new Date(); today.setHours(0, 0, 0, 0);
  function expClass(d) {
    if (!d) return "text-slate-400";
    const dt = new Date(d + "T00:00:00");
    const diff = (dt - today) / (1000 * 60 * 60 * 24);
    if (diff < 0) return "text-red-700 font-bold";
    if (diff < 30) return "text-amber-700 font-bold";
    return "text-slate-700";
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search name, plate, or ID…" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-sm w-80" data-testid="validation-search" />
        <div className="ml-auto flex items-center gap-2">
          {(user?.role === "commanding_officer" || user?.role === "super_admin" || user?.is_original_admin) && (
            <Button variant="outline" onClick={() => setPwOpen(true)} className="rounded-sm h-9 text-xs" data-testid="validation-change-pw">
              <Key size={14} weight="bold" className="mr-1" />Change Password
            </Button>
          )}
          <Button variant="outline" onClick={downloadExcel} className="rounded-sm h-9 text-xs" data-testid="validation-export">
            <FileXls size={14} weight="bold" className="mr-1" />Excel
          </Button>
          <Button variant="outline" onClick={lock} className="rounded-sm h-9 text-xs" data-testid="validation-lock">
            <Lock size={14} weight="bold" className="mr-1" />Lock
          </Button>
          <Button onClick={startNew} className="rounded-sm h-9 text-xs bg-slate-900 text-white" data-testid="validation-new">
            <Plus size={14} weight="bold" className="mr-1" />New Record
          </Button>
        </div>
      </div>

      <div className="surface-grid">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} weight="bold" className="text-emerald-700" />
            <span className="font-display font-bold">Validation Records</span>
          </div>
          <span className="tab-tag text-slate-500">{filtered.length} of {rows.length}</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-white border-b border-slate-200">
            <tr className="text-left tab-tag text-slate-500">
              <th className="px-4 py-2">First Name</th>
              <th className="px-4 py-2">Last Name</th>
              <th className="px-4 py-2">Phone</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Plate #</th>
              <th className="px-4 py-2">ID / Plaque #</th>
              <th className="px-4 py-2">Expiration</th>
              <th className="px-4 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500 text-sm">No validation records yet.</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`validation-row-${r.id}`}>
                <td className="px-4 py-2 font-medium">{r.first_name}</td>
                <td className="px-4 py-2 font-medium">{r.last_name}</td>
                <td className="px-4 py-2 mono text-xs">{r.phone || "—"}</td>
                <td className="px-4 py-2 mono text-xs">{r.email || "—"}</td>
                <td className="px-4 py-2 mono">{r.plate_number || "—"}</td>
                <td className="px-4 py-2 mono">{r.id_plaque_number || "—"}</td>
                <td className={`px-4 py-2 mono text-xs ${expClass(r.expiration_date)}`}>{r.expiration_date || "—"}</td>
                <td className="px-4 py-2 text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => startEdit(r)} className="rounded-sm h-7 text-[11px]" data-testid={`validation-edit-${r.id}`}>
                    <PencilSimple size={12} className="mr-1" />Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r)} className="rounded-sm h-7 text-[11px] text-red-600 hover:bg-red-50" data-testid={`validation-del-${r.id}`}>
                    <Trash size={12} className="mr-1" />Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <RecordDialog open={open} onOpenChange={setOpen} form={form} setForm={setForm} editing={editing} onSave={save} />
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}

function RecordDialog({ open, onOpenChange, form, setForm, editing, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader className="pb-2 border-b border-slate-200">
          <DialogTitle className="font-display text-xl">{editing ? "Edit Validation Record" : "New Validation Record"}</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">Bedford Stuyvesant Volunteer Ambulance Corps</p>
        </DialogHeader>
        <form onSubmit={onSave} className="space-y-4 pt-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="tab-tag">First Name</Label>
              <Input className="rounded-sm" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="vform-first" />
            </div>
            <div className="space-y-1.5">
              <Label className="tab-tag">Last Name</Label>
              <Input className="rounded-sm" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="vform-last" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="tab-tag">Phone</Label>
              <Input type="tel" className="rounded-sm mono" placeholder="(555) 555-5555" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="vform-phone" />
            </div>
            <div className="space-y-1.5">
              <Label className="tab-tag">Email</Label>
              <Input type="email" className="rounded-sm" placeholder="name@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="vform-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="tab-tag">Plate Number</Label>
              <Input className="rounded-sm mono" placeholder="e.g. ABC1234" value={form.plate_number} onChange={(e) => setForm({ ...form, plate_number: e.target.value.toUpperCase() })} data-testid="vform-plate" />
            </div>
            <div className="space-y-1.5">
              <Label className="tab-tag">ID / Plaque Number</Label>
              <Input className="rounded-sm mono" value={form.id_plaque_number} onChange={(e) => setForm({ ...form, id_plaque_number: e.target.value.toUpperCase() })} data-testid="vform-plaque" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="tab-tag">Expiration Date</Label>
              <Input type="date" className="rounded-sm" value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} data-testid="vform-exp" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="tab-tag">Notes</Label>
            <Textarea className="rounded-sm min-h-[80px] resize-y" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="vform-notes" />
          </div>
          <DialogFooter className="pt-3 border-t border-slate-200 gap-2">
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="vform-cancel">Cancel</Button>
            <Button type="submit" className="rounded-sm bg-slate-900 text-white" data-testid="vform-save">{editing ? "Save Changes" : "Add Record"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ChangePasswordDialog({ open, onOpenChange }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setCur(""); setNw(""); } }, [open]);

  async function submit(e) {
    e.preventDefault();
    if (nw.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/validation/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated — others must re-unlock with the new password");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Change failed");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Change Validation Password</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="tab-tag">Current Password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="rounded-sm" required /></div>
          <div><Label className="tab-tag">New Password</Label><Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} className="rounded-sm" required /></div>
          <DialogFooter className="pt-2 border-t border-slate-200">
            <Button type="submit" disabled={busy} className="rounded-sm bg-slate-900 text-white">{busy ? "Saving…" : "Update Password"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

